import React, { useState } from "react";
import { Mail, MapPin, User, Send, CheckCircle2, ExternalLink, Navigation } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import PageHero from "../components/PageHero";
import { images, club } from "../mock/mock";
import { mapsUrl } from "../lib/maps";

export default function Kontakt() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = `Name: ${form.name}\nE-Mail: ${form.email}\n\n${form.message}`;
    const mailto = `mailto:${club.emails.vorstand}?subject=${encodeURIComponent(
      form.subject || "Anfrage über die Website"
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setSent(true);
    toast({
      title: "E-Mail-Programm geöffnet",
      description:
        "Bitte die vorbereitete E-Mail in eurem E-Mail-Programm noch abschicken.",
    });
  };

  return (
    <div>
      <PageHero
        eyebrow="Schreibt uns"
        title="Kontakt"
        subtitle="Fragen zu Mitgliedschaft, Beiträgen oder unseren Gewässern? Wir freuen uns auf eure Nachricht."
        image={images.anglerMisty}
      />

      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
          {/* Info */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className="font-serif text-3xl font-600 text-foreground">
              So erreicht ihr uns
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Bei Fragen wendet euch bitte an unseren ersten Vorsitzenden
              {" "}<strong className="text-foreground">{club.chairman}</strong> oder
              nutzt die passende E-Mail-Adresse.
            </p>

            <div className="space-y-4">
              <ContactRow icon={MapPin} title="Vereinsheim">
                {club.address.heim}
                <br />
                {club.address.city}
                <br />
                <a
                  href={mapsUrl(`${club.address.heim}, ${club.address.city}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="route-link-vereinsheim"
                  className="inline-flex items-center gap-1.5 mt-2 text-primary font-600 hover:text-primary/80 transition-colors"
                >
                  <Navigation className="h-4 w-4" /> Route planen
                </a>
              </ContactRow>
              <ContactRow icon={Mail} title="Vorstand">
                <a className="hover:text-primary transition-colors break-all" href={`mailto:${club.emails.vorstand}`}>
                  {club.emails.vorstand}
                </a>
              </ContactRow>
              <ContactRow icon={Mail} title="Anfragen (Aufnahme, Beiträge)">
                <a className="hover:text-primary transition-colors break-all" href={`mailto:${club.emails.anfrage}`}>
                  {club.emails.anfrage}
                </a>
              </ContactRow>
              <ContactRow icon={Mail} title="Gewässerwart">
                <a className="hover:text-primary transition-colors break-all" href={`mailto:${club.emails.gewaesserwart}`}>
                  {club.emails.gewaesserwart}
                </a>
              </ContactRow>
            </div>

            <a
              href={club.hejfish}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-600 text-primary hover:text-primary/80 transition-colors"
            >
              Angelkarten online kaufen <ExternalLink className="h-4 w-4" />
            </a>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
              {sent ? (
                <div className="text-center py-10">
                  <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-4" />
                  <h3 className="font-serif text-2xl font-600 text-foreground">
                    Vielen Dank, {form.name || "lieber Gast"}!
                  </h3>
                  <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                    Wir haben eine E-Mail in eurem E-Mail-Programm vorbereitet.
                    Bitte schickt sie dort noch ab – wir melden uns
                    schnellstmöglich zurück.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-6 border-primary/30 text-primary hover:bg-secondary"
                    data-testid="kontakt-new-message-button"
                    onClick={() => {
                      setSent(false);
                      setForm({ name: "", email: "", subject: "", message: "" });
                    }}
                  >
                    Neue Nachricht
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input id="name" name="name" data-testid="kontakt-name-input" required value={form.name} onChange={handleChange} placeholder="Max Mustermann" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail</Label>
                      <Input id="email" name="email" data-testid="kontakt-email-input" type="email" required value={form.email} onChange={handleChange} placeholder="max@beispiel.de" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Betreff</Label>
                    <Input id="subject" name="subject" data-testid="kontakt-subject-input" required value={form.subject} onChange={handleChange} placeholder="Anfrage zur Mitgliedschaft" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Nachricht</Label>
                    <Textarea id="message" name="message" data-testid="kontakt-message-input" required rows={5} value={form.message} onChange={handleChange} placeholder="Eure Nachricht an uns ..." />
                  </div>
                  <Button type="submit" data-testid="kontakt-submit-button" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-600 gap-2 h-12">
                    <Send className="h-4 w-4" /> Nachricht senden
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    Beim Absenden öffnet sich euer E-Mail-Programm mit einer
                    fertig vorbereiteten Nachricht an den Vorstand.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactRow({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-4 bg-secondary/50 rounded-xl p-4 border border-border">
      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div>
        <div className="font-600 text-foreground text-sm">{title}</div>
        <div className="text-muted-foreground text-sm mt-0.5">{children}</div>
      </div>
    </div>
  );
}
